import { PersonServiceInterface } from 'domain/services';
import {
  GetAllPersonUseCaseInterface,
  GetByIdPersonUseCaseInterface,
  UpdateProfileUseCaseInterface,
  GetByIdProfileUseCaseInterface,
  GetAllPersonProfileUseCaseInterface,
  ChangePasswordProfileUseCaseInterface,
  UpdatePersonUseCaseInterface,
  ChangePersonPasswordUseCaseInterface,
  ActivateInactivatePersonUseCaseInterface,
  ActivateInactivatePersonProfileUseCaseInterface,
  GetAllPersonOverviewIndicatorsUseCaseInterface,
  GetPersonByIdV2UseCaseInterface,
  GetAllPersonActivitiesUseCaseInterface,
  GetAllPersonCompletedEvaluationsUseCaseInterface,
  GetCompletedEvaluationDetailsUseCaseInterface,
  DeleteCompletedEvaluationUseCaseInterface,
} from 'domain/usecases';
import { GetAllPersonValidatedActivitiesUseCaseInterface } from 'domain/usecases/person/validated-activities';

interface UseCasesInjection {
  // Person
  getAllPerson: GetAllPersonUseCaseInterface;
  getByIdPerson: GetByIdPersonUseCaseInterface;
  getAllPersonProfile: GetAllPersonProfileUseCaseInterface;
  updatePerson: UpdatePersonUseCaseInterface;
  changePersonPassword: ChangePersonPasswordUseCaseInterface;
  activateInactivatePerson: ActivateInactivatePersonUseCaseInterface;
  getAllPersonOverviewIndicators: GetAllPersonOverviewIndicatorsUseCaseInterface;
  getPersonByIdV2: GetPersonByIdV2UseCaseInterface;

  // Profile
  updateProfile: UpdateProfileUseCaseInterface;
  getByIdProfile: GetByIdProfileUseCaseInterface;
  changePasswordProfile: ChangePasswordProfileUseCaseInterface;
  activateInactivatePersonProfile: ActivateInactivatePersonProfileUseCaseInterface;

  // Activities
  getAllPersonActivities: GetAllPersonActivitiesUseCaseInterface;

  // Validated Activities
  getAllPersonValidatedActivities: GetAllPersonValidatedActivitiesUseCaseInterface;

  // Completed Evaluations
  getAllPersonCompletedEvaluations: GetAllPersonCompletedEvaluationsUseCaseInterface;
  getCompletedEvaluationDetails: GetCompletedEvaluationDetailsUseCaseInterface;
  deleteCompletedEvaluation: DeleteCompletedEvaluationUseCaseInterface;
}

export class PersonService implements PersonServiceInterface {
  // Person
  private readonly _getAllPerson: GetAllPersonUseCaseInterface;
  private readonly _getByIdPerson: GetByIdPersonUseCaseInterface;
  private readonly _getAllPersonProfile: GetAllPersonProfileUseCaseInterface;
  private readonly _updatePerson: UpdatePersonUseCaseInterface;
  private readonly _changePersonPassword: ChangePersonPasswordUseCaseInterface;
  private readonly _activateInactivatePerson: ActivateInactivatePersonUseCaseInterface;
  private readonly _getAllPersonOverviewIndicators: GetAllPersonOverviewIndicatorsUseCaseInterface;
  private readonly _getPersonByIdV2: GetPersonByIdV2UseCaseInterface;

  // Profile
  private readonly _updateProfile: UpdateProfileUseCaseInterface;
  private readonly _getByIdProfile: GetByIdProfileUseCaseInterface;
  private readonly _changePasswordProfile: ChangePasswordProfileUseCaseInterface;
  private readonly _activateInactivatePersonProfile: ActivateInactivatePersonProfileUseCaseInterface;

  // Activities
  private readonly _getAllPersonActivities: GetAllPersonActivitiesUseCaseInterface;

  // Validated Activities
  private readonly _getAllPersonValidatedActivities: GetAllPersonValidatedActivitiesUseCaseInterface;

  // Completed Evaluations
  private readonly _getAllPersonCompletedEvaluations: GetAllPersonCompletedEvaluationsUseCaseInterface;
  private readonly _getCompletedEvaluationDetails: GetCompletedEvaluationDetailsUseCaseInterface;
  private readonly _deleteCompletedEvaluation: DeleteCompletedEvaluationUseCaseInterface;

  constructor(usecases: UseCasesInjection) {
    // Person
    this._getAllPerson = usecases.getAllPerson;
    this._getByIdPerson = usecases.getByIdPerson;
    this._getAllPersonProfile = usecases.getAllPersonProfile;
    this._updatePerson = usecases.updatePerson;
    this._changePersonPassword = usecases.changePersonPassword;
    this._activateInactivatePerson = usecases.activateInactivatePerson;
    this._getAllPersonOverviewIndicators =
      usecases.getAllPersonOverviewIndicators;
    this._getPersonByIdV2 = usecases.getPersonByIdV2;

    // Profile
    this._updateProfile = usecases.updateProfile;
    this._getByIdProfile = usecases.getByIdProfile;
    this._changePasswordProfile = usecases.changePasswordProfile;
    this._activateInactivatePersonProfile =
      usecases.activateInactivatePersonProfile;

    // Activities
    this._getAllPersonActivities = usecases.getAllPersonActivities;

    // Validated Activities
    this._getAllPersonValidatedActivities =
      usecases.getAllPersonValidatedActivities;

    // Completed Evaluations
    this._getAllPersonCompletedEvaluations =
      usecases.getAllPersonCompletedEvaluations;
    this._getCompletedEvaluationDetails =
      usecases.getCompletedEvaluationDetails;
    this._deleteCompletedEvaluation = usecases.deleteCompletedEvaluation;
  }

  getAllPerson(
    params: GetAllPersonUseCaseInterface.Params,
  ): Promise<GetAllPersonUseCaseInterface.Result> {
    return this._getAllPerson.execute(params);
  }

  getByIdPerson(
    params: GetByIdPersonUseCaseInterface.Params,
  ): Promise<GetByIdPersonUseCaseInterface.Result> {
    return this._getByIdPerson.execute(params);
  }

  getAllPersonProfile(
    params: GetAllPersonProfileUseCaseInterface.Params,
  ): Promise<GetAllPersonProfileUseCaseInterface.Result> {
    return this._getAllPersonProfile.execute(params);
  }

  getByIdProfile(
    params: GetByIdProfileUseCaseInterface.Params,
  ): Promise<GetByIdProfileUseCaseInterface.Result> {
    return this._getByIdProfile.execute(params);
  }

  updateProfile(
    params: UpdateProfileUseCaseInterface.Params,
  ): Promise<UpdateProfileUseCaseInterface.Result> {
    return this._updateProfile.execute(params);
  }

  changePasswordProfile(
    params: ChangePasswordProfileUseCaseInterface.Params,
  ): Promise<ChangePasswordProfileUseCaseInterface.Result> {
    return this._changePasswordProfile.execute(params);
  }

  updatePerson(
    params: UpdatePersonUseCaseInterface.Params,
  ): Promise<UpdatePersonUseCaseInterface.Result> {
    return this._updatePerson.execute(params);
  }

  changePersonPassword(
    params: ChangePersonPasswordUseCaseInterface.Params,
  ): Promise<ChangePersonPasswordUseCaseInterface.Result> {
    return this._changePersonPassword.execute(params);
  }

  activateInactivatePerson(
    params: ActivateInactivatePersonUseCaseInterface.Params,
  ): Promise<ActivateInactivatePersonUseCaseInterface.Result> {
    return this._activateInactivatePerson.execute(params);
  }

  activateInactivatePersonProfile(
    params: ActivateInactivatePersonProfileUseCaseInterface.Params,
  ): Promise<ActivateInactivatePersonProfileUseCaseInterface.Result> {
    return this._activateInactivatePersonProfile.execute(params);
  }

  getAllPersonOverviewIndicators(
    params: GetAllPersonOverviewIndicatorsUseCaseInterface.Params,
  ): Promise<GetAllPersonOverviewIndicatorsUseCaseInterface.Result> {
    return this._getAllPersonOverviewIndicators.execute(params);
  }

  getPersonByIdV2(
    params: GetPersonByIdV2UseCaseInterface.Params,
  ): Promise<GetPersonByIdV2UseCaseInterface.Result> {
    return this._getPersonByIdV2.execute(params);
  }

  getAllPersonActivities(
    params: GetAllPersonActivitiesUseCaseInterface.Params,
  ): Promise<GetAllPersonActivitiesUseCaseInterface.Result> {
    return this._getAllPersonActivities.execute(params);
  }

  getAllPersonValidatedActivities(
    params: GetAllPersonValidatedActivitiesUseCaseInterface.Params,
  ): Promise<GetAllPersonValidatedActivitiesUseCaseInterface.Result> {
    return this._getAllPersonValidatedActivities.execute(params);
  }

  getAllPersonCompletedEvaluations(
    params: GetAllPersonCompletedEvaluationsUseCaseInterface.Params,
  ): Promise<GetAllPersonCompletedEvaluationsUseCaseInterface.Result> {
    return this._getAllPersonCompletedEvaluations.execute(params);
  }

  getCompletedEvaluationDetails(
    params: GetCompletedEvaluationDetailsUseCaseInterface.Params,
  ): Promise<GetCompletedEvaluationDetailsUseCaseInterface.Result> {
    return this._getCompletedEvaluationDetails.execute(params);
  }

  deleteCompletedEvaluation(
    params: DeleteCompletedEvaluationUseCaseInterface.Params,
  ): Promise<DeleteCompletedEvaluationUseCaseInterface.Result> {
    return this._deleteCompletedEvaluation.execute(params);
  }
}
